// Lifecycle presentation config for the property list + detail views.
//
// seller-service returns a coarse `stage` (collapsed from the 11-state property
// machine in admin-service) plus `stageStep`; the FE only maps those to
// labels/colors/copy here. i18n is not wired yet, so copy lives as plain strings
// (per repo convention).
//
// Every property transition is admin-driven, so unlike the listing-request
// lifecycle there is no "action required" stage and no seller-action guards.
import type { ChipColor } from '@uiKit/elements/Chip.vue'
import type { PiIconName } from '@uiKit/elements/PiIcon.vue'

// The coarse tracker stages emitted by the backend (PropertyStage).
export type PropertyStage =
  | 'onboarding'
  | 'in_review'
  | 'tokenising'
  | 'listed'
  | 'rejected'
  | 'archived'

// Typed stage values — the single source of truth for comparing `stage`, so a
// backend rename breaks the type-check rather than silently breaking a count.
export const PROPERTY_STAGE = {
  onboarding: 'onboarding',
  inReview: 'in_review',
  tokenising: 'tokenising',
  listed: 'listed',
  rejected: 'rejected',
  archived: 'archived',
} as const satisfies Record<string, PropertyStage>

export interface StageMeta {
  label: string
  color: ChipColor
}

export const PROPERTY_STAGE_META: Record<PropertyStage, StageMeta> = {
  onboarding: { label: 'Being prepared', color: 'dark' },
  in_review: { label: 'Under review', color: 'primary' },
  tokenising: { label: 'Tokenising', color: 'warning' },
  listed: { label: 'Live', color: 'success' },
  rejected: { label: 'Rejected', color: 'danger' },
  archived: { label: 'Archived', color: 'neutral' },
}

export const PROPERTY_STAGE_META_FALLBACK: StageMeta = { label: 'In Progress', color: 'neutral' }

export function propertyStageMetaOf(stage: string): StageMeta {
  return PROPERTY_STAGE_META[stage as PropertyStage] ?? PROPERTY_STAGE_META_FALLBACK
}

// The 4 canonical tracker steps. `stageStep` (1-4) from the backend drives
// done/active/upcoming; `stageStepCount` confirms the total so the FE can never
// disagree with the server's classification.
export interface PropertyStepMeta {
  step: number
  label: string
  caption: string
}

export const PROPERTY_STEPS: PropertyStepMeta[] = [
  { step: 1, label: 'Prepared', caption: 'Property created' },
  { step: 2, label: 'Review', caption: 'Verification & compliance' },
  { step: 3, label: 'Tokenising', caption: 'VASP onboarding' },
  { step: 4, label: 'Live', caption: 'Open to investors' },
]

// Off-path stages that change the tracker's active-step tone.
export const NEGATIVE_PROPERTY_STAGES: ReadonlySet<string> = new Set(['rejected', 'archived'])

export type LifecycleTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info'

// "What happens next" copy for the detail screen. Answers the seller's actual
// question on this page — "what am I waiting for?" — per stage. Every stage here
// is admin-driven, so none of this copy asks the seller to do anything.
export interface StageOutlook {
  headline: string
  detail: string
  icon: PiIconName
  tone: LifecycleTone
}

export const PROPERTY_STAGE_OUTLOOK: Record<PropertyStage, StageOutlook> = {
  onboarding: {
    headline: 'Being prepared',
    detail:
      'Our team is completing your property record — details, documents and financials — before it enters review.',
    icon: 'hourglass',
    tone: 'primary',
  },
  in_review: {
    headline: 'Under review',
    detail:
      'Your property is going through verification and compliance checks. Nothing is needed from you.',
    icon: 'search',
    tone: 'primary',
  },
  tokenising: {
    headline: 'Tokenisation in progress',
    detail:
      'Approved internally and now being onboarded with our regulated virtual-asset partner ahead of listing.',
    icon: 'refresh',
    tone: 'warning',
  },
  listed: {
    headline: 'Live on the marketplace',
    detail: 'Your property is approved and open to investors. Funding progress is shown above.',
    icon: 'verified',
    tone: 'success',
  },
  rejected: {
    headline: 'Not approved',
    detail: 'This property did not pass review. Your relationship manager can walk you through it.',
    icon: 'times-circle',
    tone: 'danger',
  },
  archived: {
    headline: 'Archived',
    detail: 'This property is no longer active on the platform.',
    icon: 'ban',
    tone: 'neutral',
  },
}

export const PROPERTY_STAGE_OUTLOOK_FALLBACK: StageOutlook = {
  headline: 'In progress',
  detail: 'We’re working through this property. Its status updates here as it advances.',
  icon: 'clock',
  tone: 'primary',
}

export function propertyStageOutlookOf(stage: string): StageOutlook {
  return PROPERTY_STAGE_OUTLOOK[stage as PropertyStage] ?? PROPERTY_STAGE_OUTLOOK_FALLBACK
}

// ── Funding (offerings) ────────────────────────────────────────────────────

// Funding sub-state of the property's primary offering
// (OfferingProcessingStatus). `null` means the property has no offering yet —
// which is "not raising", NOT "0% funded".
export const FUNDING_STATUS_META: Record<string, StageMeta> = {
  pending: { label: 'Funding pending', color: 'neutral' },
  open: { label: 'Live funding', color: 'success' },
  filled: { label: 'Fully funded', color: 'primary' },
  completed: { label: 'Funding complete', color: 'primary' },
}

export function fundingStatusMetaOf(status: string | null): StageMeta | null {
  if (!status) return null
  return FUNDING_STATUS_META[status] ?? null
}

// The funding sub-states that mean the raise is finished. Mirrors the backend's
// `isFullyFunded` predicate, which in turn mirrors admin-service's own bucket.
export const FULLY_FUNDED_STATUSES: ReadonlySet<string> = new Set(['filled', 'completed'])

// Human labels for property types (PropertyType).
export const PROPERTY_TYPE_LABELS: Record<string, string> = {
  residential: 'Residential',
  commercial: 'Commercial',
  apartment: 'Apartment',
  villa: 'Villa',
  building: 'Building',
  store: 'Store',
  workshop: 'Workshop',
  land: 'Land',
}

export function propertyTypeLabelOf(type: string): string {
  return PROPERTY_TYPE_LABELS[type] ?? type.replace(/^./, (char) => char.toUpperCase())
}

// Human labels for document types (PropertyDocumentType). Fallback prettifies
// any unknown snake_case type rather than showing the raw enum.
export const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  title_deed: 'Title deed',
  valuation_report: 'Valuation report',
  brochure: 'Brochure',
  noc: 'NOC',
  makani: 'Makani',
  rent_agreement: 'Rent agreement',
  ownership_doc: 'Ownership document',
  white_paper: 'White paper',
  investment_memorandum: 'Investment memorandum',
  proforma: 'Proforma',
  other: 'Other',
}

export function documentTypeLabelOf(type: string): string {
  return (
    DOCUMENT_TYPE_LABELS[type] ??
    type.replace(/_/g, ' ').replace(/^./, (char) => char.toUpperCase())
  )
}

// Document review status → chip (PropertyContentStatus).
export const DOCUMENT_STATUS_META: Record<string, StageMeta> = {
  pending_review: { label: 'Pending review', color: 'warning' },
  approved: { label: 'Approved', color: 'success' },
  rejected: { label: 'Rejected', color: 'danger' },
}

export function documentStatusMetaOf(status: string): StageMeta {
  return DOCUMENT_STATUS_META[status] ?? PROPERTY_STAGE_META_FALLBACK
}

// Page size for the paginated list (useInfiniteQuery).
export const PROPERTIES_PAGE_LIMIT = 10
